/**
 * CambrianTutorial — the first run eases the sea in instead of lecturing.
 *
 * Phases (each gates what may spawn, via `allows`):
 *   feed    — only plain plankton. A small hint shows how to bite; it
 *             fades once you've eaten a few. Lasts until you've eaten a
 *             dozen (or a minute passes).
 *   hunters — bigger creatures start to fill in. The first time a hunter
 *             winds up near you, time slows and a hint shows how to dodge;
 *             it clears after you've dodged both ways.
 *   poison  — toxic siphonophores join the sea (a couple spawn nearby,
 *             marked); a hint warns not to eat them.
 *   heal    — a glow medusa appears nearby, marked; eat it to heal.
 * Then the tutorial ends and the full sea is on. The codex card for each
 * newcomer shows as it's introduced (see `codexAllowed`).
 */

import type { CreatureDef } from './types';
import { IS_TOUCH } from '@/ui/TouchControls';

export type TutorialEvent =
  | { kind: 'eat'; defId: string }
  | { kind: 'flick'; side: 1 | -1 }
  /** A hunter started its strike wind-up close to the player. */
  | { kind: 'threat' };

export type TutorialPhase = 'feed' | 'hunters' | 'poison' | 'heal' | 'done';

export interface TutorialHooks {
  /** Spawn one creature of this def near the player; returns its entity id. */
  spawnNear: (defId: string) => number;
  /** Point the on-screen marker at an entity (0 = clear). */
  mark: (entityId: number) => void;
  /** Brief slow-motion so a hint can land. */
  slowMo: () => void;
  onDone: () => void;
}

const FEED_EATS = 12;
const FEED_MAX_SEC = 60;
const HUNTERS_MIN_SEC = 12;
const HUNTERS_MAX_SEC = 75;
const THREAT_HINT_AFTER = 8;   // show the dodge hint anyway after this long
const POISON_SEC = 18;
const HEAL_MAX_SEC = 30;

export class CambrianTutorial {
  phase: TutorialPhase = 'feed';
  private hooks: TutorialHooks;
  private root: HTMLElement;
  private t = 0;
  private eats = 0;
  private dodgedRight = false;
  private dodgedLeft = false;
  private dodgeHintShown = false;

  constructor(hooks: TutorialHooks) {
    this.hooks = hooks;
    this.injectStyle();
    this.root = document.createElement('div');
    this.root.id = 'cambrian-hint';
    document.body.appendChild(this.root);
  }

  /** Called once the opening cinematic is over and play begins. */
  start(): void {
    this.hint(IS_TOUCH ? ['BITE'] : ['Left-click', 'Space'], 'Lunge at the plankton to eat');
  }

  get active(): boolean { return this.phase !== 'done'; }

  /** May this creature spawn yet? */
  allows(def: CreatureDef): boolean {
    switch (this.phase) {
      case 'feed': return def.tier === 1 && !def.effect;
      case 'hunters': return !def.effect;
      case 'poison': return def.effect !== 'heal';
      default: return true;
    }
  }

  /** Codex cards that belong to the current phase (shown as things appear). */
  codexAllowed(codexId: string): boolean {
    const id = codexId.replace('cambrian.creature.', '');
    switch (this.phase) {
      case 'feed': return id === 'plankton_bloom' && this.t > 3;
      case 'hunters': return ['hunting_worm', 'proto_trilobite', 'marrella'].includes(id) && this.dodgeHintShown;
      case 'poison': return id === 'toxic_siphon';
      case 'heal': return id === 'glow_medusa';
      default: return false;
    }
  }

  /** Game-time tick (only while unpaused). */
  tick(dt: number): void {
    if (!this.active) return;
    this.t += dt;
    switch (this.phase) {
      case 'feed':
        if (this.eats >= FEED_EATS || this.t >= FEED_MAX_SEC) this.enter('hunters');
        break;
      case 'hunters':
        if (!this.dodgeHintShown && this.t >= THREAT_HINT_AFTER) this.showDodgeHint(false);
        if ((this.dodgedRight && this.dodgedLeft && this.t >= HUNTERS_MIN_SEC) || this.t >= HUNTERS_MAX_SEC) this.enter('poison');
        break;
      case 'poison':
        if (this.t >= POISON_SEC) this.enter('heal');
        break;
      case 'heal':
        if (this.t >= HEAL_MAX_SEC) this.finish();
        break;
    }
  }

  report(e: TutorialEvent): void {
    if (!this.active) return;
    if (e.kind === 'eat') {
      if (this.phase === 'feed' && e.defId !== 'toxic_siphon' && e.defId !== 'glow_medusa') {
        this.eats++;
        if (this.eats === 3) this.hide();
      }
      if (e.defId === 'toxic_siphon') this.hint([], 'That was poison! Leave the violet spiky ones alone.', 'warn', 5);
      if (e.defId === 'glow_medusa' && this.phase === 'heal') this.finish();
    } else if (e.kind === 'flick') {
      if (e.side === 1) this.dodgedRight = true; else this.dodgedLeft = true;
      if (this.phase === 'hunters' && this.dodgeHintShown) {
        if (this.dodgedRight && this.dodgedLeft) this.hint([], 'Nice dodging. Grow bigger and the hunters become prey.', 'ok', 4);
        else this.showDodgeHint(true);
      }
    } else if (e.kind === 'threat') {
      if (this.phase === 'hunters' && !this.dodgeHintShown) this.showDodgeHint(false);
    }
  }

  skip(): void { this.finish(); }

  destroy(): void {
    this.root.remove();
    this.phase = 'done';
  }

  private enter(phase: TutorialPhase): void {
    this.phase = phase;
    this.t = 0;
    this.hooks.mark(0);
    if (phase === 'hunters') {
      this.hide();
    } else if (phase === 'poison') {
      const a = this.hooks.spawnNear('toxic_siphon');
      this.hooks.spawnNear('toxic_siphon');
      this.hooks.mark(a);
      this.hint([], "Don't eat the violet, spiky siphonophores — they're poisonous.", 'warn', 10);
    } else if (phase === 'heal') {
      this.hooks.mark(this.hooks.spawnNear('glow_medusa'));
      this.hint([], 'A glow medusa! Eat the glowing jellies to heal.', 'ok');
    }
  }

  private showDodgeHint(hasDodged: boolean): void {
    if (!this.dodgeHintShown) this.hooks.slowMo();
    this.dodgeHintShown = true;
    const need = !this.dodgedRight ? 'right' : 'left';
    const keys = IS_TOUCH
      ? [need === 'right' ? 'DODGE R' : 'DODGE L']
      : [need === 'right' ? 'Right-click' : 'Double right-click'];
    const text = hasDodged
      ? 'Now dodge the other way.'
      : 'Hunters strike after a wind-up — dodge sideways. Dodges cost ATP.';
    this.hint(keys, `${text} (dodge ${need})`);
  }

  private finish(): void {
    if (this.phase === 'done') return;
    this.phase = 'done';
    this.hooks.mark(0);
    this.hint([], 'The whole sea is open now. Eat, evolve, survive.', 'ok', 5);
    window.setTimeout(() => this.root.remove(), 6000);
    this.hooks.onDone();
  }

  /** Show a small hint pill; `hideAfter` seconds (wall time) auto-hides it. */
  private hideTimer = 0;
  private hint(keys: string[], text: string, tone: 'info' | 'warn' | 'ok' = 'info', hideAfter = 0): void {
    const k = keys.map((x) => `<span class="ch-key">${x}</span>`).join('<span class="ch-or">or</span>');
    this.root.innerHTML = `<div class="ch-pill ch-${tone}">${k}<span class="ch-text">${text}</span></div>`;
    this.root.classList.add('show');
    window.clearTimeout(this.hideTimer);
    if (hideAfter > 0) this.hideTimer = window.setTimeout(() => this.hide(), hideAfter * 1000);
  }

  private hide(): void {
    this.root.classList.remove('show');
  }

  private injectStyle(): void {
    if (document.getElementById('cambrian-hint-styles')) return;
    const style = document.createElement('style');
    style.id = 'cambrian-hint-styles';
    style.textContent = `
#cambrian-hint { position: fixed; left: 50%; bottom: 96px; transform: translate(-50%, 12px); z-index: 60; opacity: 0; transition: opacity 0.4s, transform 0.4s; pointer-events: none; font-family: 'Inter', system-ui, sans-serif; }
#cambrian-hint.show { opacity: 1; transform: translate(-50%, 0); }
#cambrian-hint .ch-pill { display: flex; align-items: center; gap: 8px; padding: 10px 16px; border-radius: 999px; background: rgba(10, 8, 4, 0.78); border: 1px solid rgba(255, 208, 112, 0.45); color: #fff5dc; box-shadow: 0 8px 24px rgba(0,0,0,0.4); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); white-space: nowrap; }
#cambrian-hint .ch-warn { border-color: rgba(210, 110, 255, 0.7); box-shadow: 0 8px 24px rgba(0,0,0,0.4), 0 0 18px rgba(200, 90, 255, 0.3); }
#cambrian-hint .ch-ok { border-color: rgba(120, 255, 150, 0.6); }
#cambrian-hint .ch-key { font-size: 11px; font-weight: 800; white-space: nowrap; padding: 3px 8px; border-radius: 6px; background: rgba(255, 220, 160, 0.16); border: 1px solid rgba(255, 220, 160, 0.4); }
#cambrian-hint .ch-or { font-size: 10px; opacity: 0.55; }
#cambrian-hint .ch-text { font-size: 14px; font-weight: 600; }
@media (max-width: 760px), (max-height: 500px) {
  #cambrian-hint .ch-pill { white-space: normal; max-width: calc(100vw - 32px); padding: 8px 12px; }
  #cambrian-hint .ch-text { font-size: 12px; }
}
/* Landscape phone: a slim pill, not a banner. */
@media (max-height: 500px) {
  /* Bottom centre, in the gap between the DODGE and BITE buttons. */
  html body.has-pad #cambrian-hint { bottom: calc(14px + env(safe-area-inset-bottom)); }
  #cambrian-hint .ch-pill { max-width: min(380px, calc(100vw - 400px)); padding: 4px 10px; gap: 5px; border-radius: 12px; }
  #cambrian-hint .ch-text { font-size: 10px; font-weight: 600; line-height: 1.3; }
  #cambrian-hint .ch-key { font-size: 8px; padding: 2px 5px; border-radius: 4px; }
  #cambrian-hint .ch-or { font-size: 8px; }
}
`;
    document.head.appendChild(style);
  }
}
